import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnalytics, useEmailStats } from '../hooks/useApi';
import type { AnalyticsResponse } from '../types';
import {
  deltaPct, funnelPct, donutPct, inboxRate, barHeightPct, donutSegments, flagEmoji, toCsv,
} from '../lib/analyticsMath';

type Range = '7d' | '30d' | '90d' | '365d';

/* ---------- CSV export ---------- */

// Build a CSV (pure toCsv) and trigger a client-side download. No dependency, no server round-trip.
function downloadCsv(filename: string, headers: string[], rows: (string | number)[][]): void {
  const blob = new Blob([toCsv(headers, rows)], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// number|null percentage → "42%" or "" (blank when there's no denominator).
const pctHint = (n: number | null): string => (n == null ? '' : `${n}%`);

/* ---------- info tooltip ---------- */

// Small circled-"i" that reveals an explanation of how a number/chart is computed.
// Hover (desktop) or tap (touch) to open; Esc / blur / outside-hover closes. `align`
// controls which way the bubble opens so it never runs off-screen at the edges.
function InfoTip({ text, align = 'left' }: { text: string; align?: 'left' | 'right' }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-flex align-middle"
      onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        type="button"
        aria-label={text}
        aria-expanded={open}
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
        onBlur={() => setOpen(false)}
        className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-gray-300 text-[10px] font-bold normal-case leading-none text-gray-400 transition-colors hover:border-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-300"
      >i</button>
      {open && (
        <span
          role="tooltip"
          className={`absolute top-full z-30 mt-2 w-60 max-w-[min(15rem,80vw)] rounded-lg bg-gray-900 px-3 py-2 text-left text-[11px] font-normal normal-case leading-snug tracking-normal text-white shadow-xl ${align === 'right' ? 'right-0' : 'left-0'}`}
        >{text}</span>
      )}
    </span>
  );
}

/* ---------- dependency-free chart primitives ---------- */

// Multi-series daily bar chart. Each series is a colored bar per day.
function MultiBar({ series, keys }: {
  series: Record<string, number | string>[];
  keys: { key: string; color: string; label: string }[];
}) {
  const max = Math.max(1, ...series.flatMap(s => keys.map(k => Number(s[k.key]) || 0)));
  return (
    <div className="mt-4 flex h-40 items-end gap-1.5 overflow-x-auto pb-1">
      {series.map((s, i) => (
        <div key={i} className="flex h-full min-w-[7px] flex-1 items-end justify-center gap-0.5"
          title={`${s.date}: ${keys.map(k => `${Number(s[k.key]) || 0} ${k.label.toLowerCase()}`).join(' · ')}`}>
          {keys.map(k => (
            <div key={k.key} className="w-1.5 rounded-t" style={{ background: k.color, height: `${barHeightPct(Number(s[k.key]) || 0, max)}%` }} />
          ))}
        </div>
      ))}
    </div>
  );
}

function Legend({ keys }: { keys: { color: string; label: string }[] }) {
  return (
    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
      {keys.map(k => (
        <span key={k.label} className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm" style={{ background: k.color }} />{k.label}
        </span>
      ))}
    </div>
  );
}

function Kpi({ label, value, sub, delta, accent, info, infoAlign }: { label: string; value: number; sub?: string; delta?: number | null; accent?: boolean; info?: string; infoAlign?: 'left' | 'right' }) {
  const showDelta = delta !== null && delta !== undefined && Number.isFinite(delta);
  const up = (delta ?? 0) >= 0;
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <p className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}{info && <InfoTip text={info} align={infoAlign} />}</p>
      <p className={`mt-1 text-2xl font-bold ${accent ? 'text-primary-700' : 'text-gray-900'}`}>{value.toLocaleString()}</p>
      <div className="flex items-center gap-2">
        {sub && <p className="text-xs text-gray-400">{sub}</p>}
        {showDelta && (
          <span className={`text-[11px] font-semibold ${up ? 'text-green-600' : 'text-red-500'}`}>
            {up ? '▲' : '▼'} {Math.abs(delta as number)}%
          </span>
        )}
      </div>
    </div>
  );
}

// Horizontal-bar list (funnel steps, actions breakdown, share networks).
function HBars({ rows, color = '#7c3aed' }: { rows: { label: string; value: number; hint?: string }[]; color?: string }) {
  const max = Math.max(1, ...rows.map(r => r.value));
  return (
    <div className="mt-3 space-y-2.5">
      {rows.map(r => (
        <div key={r.label}>
          <div className="flex items-center justify-between text-xs">
            <span className="font-medium text-gray-700">{r.label}</span>
            <span className="text-gray-500">{r.value.toLocaleString()}{r.hint ? <span className="ml-1 text-gray-400">{r.hint}</span> : null}</span>
          </div>
          <div className="mt-1 h-2 rounded-full bg-gray-100">
            <div className="h-2 rounded-full" style={{ width: `${barHeightPct(r.value, max, 3)}%`, background: color }} />
          </div>
        </div>
      ))}
    </div>
  );
}

const NETWORK_COLORS: Record<string, string> = {
  linkedin: '#0a66c2', twitter: '#111827', facebook: '#1877f2', whatsapp: '#25d366',
  email: '#f59e0b', telegram: '#229ed9', copy: '#8b5cf6',
};

// Donut (conic-gradient) for share breakdown.
function Donut({ data }: { data: { label: string; value: number; color: string }[] }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  const segs = donutSegments(data.filter(d => d.value > 0));
  const stops = total === 0 ? 'var(--tw-gray-200, #e5e7eb) 0 100%' : segs.map(s => `${s.color} ${s.from}% ${s.to}%`).join(', ');
  return (
    <div className="mt-2 flex items-center gap-5">
      <div className="relative h-28 w-28 flex-none rounded-full" style={{ background: `conic-gradient(${stops})` }}>
        <div className="absolute inset-[22%] grid place-items-center rounded-full bg-white text-center">
          <span className="text-lg font-bold text-gray-900">{total.toLocaleString()}</span>
        </div>
      </div>
      <div className="min-w-0 flex-1 space-y-1.5">
        {[...segs].sort((a, b) => b.value - a.value).map(d => (
          <div key={d.label} className="flex items-center justify-between text-xs">
            <span className="inline-flex items-center gap-1.5 text-gray-700"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: d.color }} />{d.label}</span>
            <span className="text-gray-500">{d.value.toLocaleString()} · {donutPct(d.value, total)}%</span>
          </div>
        ))}
        {total === 0 && <p className="text-xs text-gray-400">—</p>}
      </div>
    </div>
  );
}

function Card({ title, right, children, info }: { title: string; right?: React.ReactNode; children: React.ReactNode; info?: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5">
      <div className="flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-1.5 text-sm font-semibold text-gray-700">{title}{info && <InfoTip text={info} />}</h2>
        {right}
      </div>
      {children}
    </div>
  );
}

/* ---------- Overview tab ---------- */

function Overview({ range }: { range: Range }) {
  const { t } = useTranslation('common');
  const { data, isLoading, error } = useAnalytics(range);

  if (isLoading) return <div className="mt-10 text-sm text-gray-400">{t('common.loading', 'Loading…')}</div>;
  if (error) return <div className="mt-10 text-sm text-red-500">{t('analytics.error', 'Could not load analytics.')}</div>;
  if (!data) return null;

  const d: AnalyticsResponse = data;
  const k = d.kpis;
  const delta = deltaPct;

  if (k.issued === 0 && k.views === 0 && d.totals.issued === undefined) {
    return (
      <div className="mt-8 rounded-xl border border-dashed border-gray-200 bg-white p-10 text-center text-sm text-gray-500">
        {t('analytics.emptyOverview', 'No credential activity in this window yet. Issue and send a credential to start seeing issuance, views, and shares here.')}
      </div>
    );
  }

  const netRows = Object.entries(d.sharesByNetwork).map(([net, value]) => ({
    label: t(`analytics.net.${net}`, net.charAt(0).toUpperCase() + net.slice(1)),
    value, color: NETWORK_COLORS[net] || '#9ca3af',
  }));
  const actionLabels: Record<string, string> = {
    view: t('analytics.act.view', 'Views'),
    click_verify: t('analytics.act.verify', 'Verify clicks'),
    linkedin_add: t('analytics.act.linkedin', 'Add to LinkedIn'),
    pdf: t('analytics.act.pdf', 'PDF downloads'),
    download: t('analytics.act.download', 'Image downloads'),
  };

  return (
    <>
      {/* KPI row */}
      <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <Kpi label={t('analytics.kpi.issued', 'Issued')} value={k.issued} delta={delta(k.issued, k.prev.issued)}
          info={t('analytics.info.issued', 'Credentials you created in this period — every credential issued, whether or not it was emailed.')} />
        <Kpi label={t('analytics.kpi.delivered', 'Delivered')} value={k.delivered} sub={`${k.deliveryRate}%`} delta={delta(k.delivered, k.prev.delivered)}
          info={t('analytics.info.delivered', 'Credentials whose notification email was sent to the recipient. Percentage is delivered ÷ issued.')} />
        <Kpi label={t('analytics.kpi.viewed', 'Viewed')} value={k.viewedAwards} sub={`${k.viewRate}% ${t('analytics.ofDelivered', 'of delivered')}`} accent
          info={t('analytics.info.viewed', 'Distinct credentials opened at least once — counted the first time each is viewed, not on every refresh. Percentage is viewed ÷ delivered.')} />
        <Kpi label={t('analytics.kpi.shared', 'Shared')} value={k.sharedAwards} sub={`${k.shareRate}% ${t('analytics.ofDelivered', 'of delivered')}`} accent infoAlign="right"
          info={t('analytics.info.shared', 'Distinct credentials shared at least once on any network. Percentage is shared ÷ delivered.')} />
        <Kpi label={t('analytics.kpi.views', 'Total views')} value={k.views} delta={delta(k.views, k.prev.views)} infoAlign="right"
          info={t('analytics.info.views', 'Every view of a credential page, including repeat visits and refreshes. (Viewed counts distinct credentials; this counts all views.)')} />
        <Kpi label={t('analytics.kpi.shares', 'Total shares')} value={k.shares} delta={delta(k.shares, k.prev.shares)} infoAlign="right"
          info={t('analytics.info.totalShares', 'Every share action across all networks, including the same credential shared more than once.')} />
      </div>

      {/* time-series */}
      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <Card title={t('analytics.issuedTrend', 'Credentials issued')}
          info={t('analytics.info.issuedTrend', 'Credentials issued vs delivered, per day, over the selected period.')}
          right={<Legend keys={[{ color: '#c4b5fd', label: t('analytics.kpi.issued', 'Issued') }, { color: '#7c3aed', label: t('analytics.kpi.delivered', 'Delivered') }]} />}>
          <MultiBar series={d.series} keys={[
            { key: 'issued', color: '#c4b5fd', label: t('analytics.kpi.issued', 'Issued') },
            { key: 'delivered', color: '#7c3aed', label: t('analytics.kpi.delivered', 'Delivered') },
          ]} />
        </Card>
        <Card title={t('analytics.engagementTrend', 'Engagement over time')}
          info={t('analytics.info.engagementTrend', 'Total views vs total shares, per day, over the selected period.')}
          right={<Legend keys={[{ color: '#10b981', label: t('analytics.act.view', 'Views') }, { color: '#f59e0b', label: t('analytics.kpi.shares', 'Shares') }]} />}>
          <MultiBar series={d.series} keys={[
            { key: 'view', color: '#10b981', label: t('analytics.act.view', 'Views') },
            { key: 'share', color: '#f59e0b', label: t('analytics.kpi.shares', 'Shares') },
          ]} />
        </Card>
      </div>

      {/* funnel + shares + actions */}
      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Card title={t('analytics.funnel.title', 'Credential lifecycle')}
          info={t('analytics.info.funnel', 'How credentials progress: issued → delivered → viewed → shared. Viewed and Shared count distinct credentials; each percentage is of the step above.')}>
          <HBars rows={[
            { label: t('analytics.funnel.issued', 'Issued'), value: d.funnel.issued },
            { label: t('analytics.funnel.delivered', 'Delivered'), value: d.funnel.delivered, hint: pctHint(funnelPct(d.funnel.delivered, d.funnel.issued)) },
            { label: t('analytics.funnel.viewed', 'Viewed'), value: d.funnel.viewed, hint: pctHint(funnelPct(d.funnel.viewed, d.funnel.delivered)) },
            { label: t('analytics.funnel.shared', 'Shared'), value: d.funnel.shared, hint: pctHint(funnelPct(d.funnel.shared, d.funnel.delivered)) },
          ]} />
        </Card>
        <Card title={t('analytics.shares.title', 'Shares by network')}
          info={t('analytics.info.sharesByNetwork', 'Share actions split by network. Counts every share, so it can exceed the number of distinct credentials shared.')}>
          <Donut data={netRows} />
        </Card>
        <Card title={t('analytics.actions.title', 'What recipients do')}
          info={t('analytics.info.actions', 'What recipients do on a credential page — views, verify clicks, Add to LinkedIn, and PDF/image downloads.')}>
          <HBars color="#10b981" rows={Object.entries(d.actions).map(([key, value]) => ({ label: actionLabels[key] || key, value }))} />
        </Card>
      </div>

    </>
  );
}

// Ranked table for the Badges / Recipients tabs.
function LeaderTable({ headers, rows, caption, info }: { headers: string[]; rows: (string | number)[][]; caption?: string; info?: string }) {
  const head = (caption || info) && (
    <div className="mb-2 mt-6 flex items-center gap-1.5 text-sm font-semibold text-gray-700">{caption}{info && <InfoTip text={info} />}</div>
  );
  if (rows.length === 0) {
    return <div>{head}<div className="rounded-xl border border-dashed border-gray-200 bg-white p-10 text-center text-sm text-gray-500">—</div></div>;
  }
  return (
    <div>
      {head}
      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-gray-100">
            {headers.map((h, i) => (
              <th key={h} className={`px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500 ${i === 0 ? '' : 'text-right'}`}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-gray-50 last:border-0">
              {r.map((c, j) => (
                <td key={j} className={`px-4 py-3 ${j === 0 ? 'font-medium text-gray-900 capitalize' : 'text-right tabular-nums text-gray-600'}`}>{typeof c === 'number' ? c.toLocaleString() : c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function BadgesTab({ range }: { range: Range }) {
  const { t } = useTranslation('common');
  const { data, isLoading } = useAnalytics(range);
  if (isLoading) return <div className="mt-10 text-sm text-gray-400">{t('common.loading', 'Loading…')}</div>;
  return (
    <LeaderTable
      caption={t('analytics.badges.caption', 'Top badges')}
      info={t('analytics.info.topBadges', 'Your badges ranked by views. Issued = how many were awarded; Views and Shares count all views/shares across their credentials.')}
      headers={[t('analytics.badges.name', 'Badge'), t('analytics.kpi.issued', 'Issued'), t('analytics.act.view', 'Views'), t('analytics.kpi.shares', 'Shares')]}
      rows={(data?.topBadges ?? []).map(b => [b.name || '—', b.issued, b.views, b.shares])}
    />
  );
}

function RecipientsTab({ range }: { range: Range }) {
  const { t } = useTranslation('common');
  const { data, isLoading } = useAnalytics(range);
  if (isLoading) return <div className="mt-10 text-sm text-gray-400">{t('common.loading', 'Loading…')}</div>;
  return (
    <LeaderTable
      caption={t('analytics.recipients.caption', 'Top recipients')}
      info={t('analytics.info.topRecipients', 'Recipients ranked by engagement. Views and Shares count actions on the credentials they received.')}
      headers={[t('analytics.recipients.name', 'Recipient'), t('analytics.act.view', 'Views'), t('analytics.kpi.shares', 'Shares')]}
      rows={(data?.topRecipients ?? []).map(r => [r.name || '—', r.views, r.shares])}
    />
  );
}

// Locale-aware country name (built-in Intl, no data shipped). Falls back to the code.
function countryName(cc: string, lang: string): string {
  try { return new Intl.DisplayNames([lang], { type: 'region' }).of(cc.toUpperCase()) || cc; }
  catch { return cc; }
}

function GeographyTab({ range }: { range: Range }) {
  const { t, i18n } = useTranslation('common');
  const { data, isLoading } = useAnalytics(range);
  if (isLoading) return <div className="mt-10 text-sm text-gray-400">{t('common.loading', 'Loading…')}</div>;
  const geo = data?.geography ?? [];
  if (geo.length === 0) {
    return (
      <div className="mt-8 rounded-xl border border-dashed border-gray-200 bg-white p-10 text-center text-sm text-gray-500">
        {t('analytics.geo.empty', 'No location data yet. Viewer country is estimated from the timezone once your credentials start getting viewed.')}
      </div>
    );
  }
  const total = geo.reduce((s, g) => s + g.views + g.shares, 0) || 1;
  const top = geo.slice(0, 12);
  return (
    <div className="mt-6 grid gap-4 lg:grid-cols-2">
      <Card title={t('analytics.geo.title', 'Top countries')}
        info={t('analytics.info.geography', 'Where your credentials are viewed and shared, estimated from each viewer’s device timezone — a privacy-friendly approximation, not a precise IP lookup.')}>
        <HBars color="#0ea5e9" rows={top.map(g => ({
          label: `${flagEmoji(g.cc)} ${countryName(g.cc, i18n.language)}`,
          value: g.views + g.shares,
          hint: `${Math.round((100 * (g.views + g.shares)) / total)}%`,
        }))} />
      </Card>
      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-gray-100">
              {[t('analytics.geo.country', 'Country'), t('analytics.act.view', 'Views'), t('analytics.kpi.shares', 'Shares')].map((h, i) => (
                <th key={h} className={`px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500 ${i === 0 ? '' : 'text-right'}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {geo.map(g => (
              <tr key={g.cc} className="border-b border-gray-50 last:border-0">
                <td className="px-4 py-3 font-medium text-gray-900">{flagEmoji(g.cc)} {countryName(g.cc, i18n.language)}</td>
                <td className="px-4 py-3 text-right tabular-nums text-gray-600">{g.views.toLocaleString()}</td>
                <td className="px-4 py-3 text-right tabular-nums text-gray-600">{g.shares.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-gray-400 lg:col-span-2">
        {t('analytics.geo.note', 'Country is estimated from the viewer’s device timezone — a privacy-friendly approximation, not a precise IP lookup.')}
      </p>
    </div>
  );
}

/* ---------- Email tab (existing) ---------- */

function EmailBarChart({ series }: { series: { date: string; sent: number; opened: number }[] }) {
  const max = Math.max(1, ...series.map(s => s.sent));
  return (
    <div className="mt-4 flex h-40 items-end gap-1.5 overflow-x-auto pb-1">
      {series.map(s => (
        <div key={s.date} className="flex h-full min-w-[8px] flex-1 items-end justify-center gap-0.5" title={`${s.date}: ${s.opened} opened / ${s.sent} sent`}>
          <div className="w-1.5 rounded-t bg-gray-200" style={{ height: `${Math.max(2, (s.sent / max) * 100)}%` }} />
          <div className="w-1.5 rounded-t bg-green-500" style={{ height: `${Math.max(1, (s.opened / max) * 100)}%` }} />
        </div>
      ))}
    </div>
  );
}

function EmailTab({ range }: { range: Range }) {
  const { t } = useTranslation('common');
  const emailRange = range === '365d' ? '90d' : range === '7d' ? '30d' : range; // email endpoint supports 30/90
  const { data, isLoading, error } = useEmailStats(emailRange);
  if (isLoading) return <div className="mt-10 text-sm text-gray-400">{t('common.loading', 'Loading…')}</div>;
  if (error) return <div className="mt-10 text-sm text-red-500">{t('analytics.error', 'Could not load analytics.')}</div>;
  if (!data) return null;
  if (data.totals.sent === 0) {
    return (
      <div className="mt-8 rounded-xl border border-dashed border-gray-200 bg-white p-10 text-center text-sm text-gray-500">
        {t('analytics.empty', 'No email activity yet in this window. Issue a credential with the email notification on to start seeing delivery and opens here.')}
      </div>
    );
  }
  const bounceRows = Object.entries(data.bounceReasons || {});
  const reasonLabel: Record<string, string> = {
    Permanent: t('analytics.bounce.permanent', 'Permanent (hard)'),
    Transient: t('analytics.bounce.transient', 'Transient (soft)'),
    Undetermined: t('analytics.bounce.undetermined', 'Undetermined'),
  };
  return (
    <>
      <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <Kpi label={t('analytics.sent', 'Sent')} value={data.totals.sent}
          info={t('analytics.info.emailSent', 'Notification emails sent for your credentials in this period.')} />
        <Kpi label={t('analytics.delivered', 'Delivered')} value={data.totals.delivered} sub={`${data.rates.delivery}%`}
          info={t('analytics.info.emailDelivered', 'Emails accepted by the recipient’s mail server. Percentage is delivered ÷ sent.')} />
        <Kpi label={t('analytics.opened', 'Opened')} value={data.totals.opened} sub={`${data.rates.open}% ${t('analytics.ofDelivered', 'of delivered')}`} accent
          info={t('analytics.info.emailOpened', 'Emails the recipient opened (tracking pixel). Percentage is opened ÷ delivered. Indicative — some clients block or prefetch tracking.')} />
        <Kpi label={t('analytics.bounced', 'Bounced')} value={data.totals.bounced} sub={`${data.rates.bounce}%`} infoAlign="right"
          info={t('analytics.info.emailBounced', 'Emails that couldn’t be delivered (bad address, full mailbox, etc.). Percentage is bounced ÷ sent.')} />
        <Kpi label={t('analytics.complained', 'Complaints')} value={data.totals.complained} sub={`${data.rates.complaint}%`} infoAlign="right"
          info={t('analytics.info.emailComplaints', 'Recipients who marked the email as spam. Percentage is complaints ÷ sent.')} />
        <Kpi label={t('analytics.deliveredNet', 'Inbox rate')} value={inboxRate(data.rates.delivery, data.rates.bounce)} sub="%" infoAlign="right"
          info={t('analytics.info.inboxRate', 'Estimated share of sent emails that reached the inbox — delivered% minus bounced%.')} />
      </div>
      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Card title={t('analytics.trend', 'Sent vs opened')}
            info={t('analytics.info.emailTrend', 'Emails sent vs opened, per day, over the period.')}
            right={<Legend keys={[{ color: '#d1d5db', label: t('analytics.sent', 'Sent') }, { color: '#22c55e', label: t('analytics.opened', 'Opened') }]} />}>
            <EmailBarChart series={data.series} />
          </Card>
        </div>
        <Card title={t('analytics.bounce.title', 'Why emails failed')}
          info={t('analytics.info.emailFailed', 'Failed emails split by bounce type (permanent/transient) plus spam complaints.')}>
          {bounceRows.length + data.totals.complained === 0 ? (
            <p className="mt-3 text-sm text-gray-400">{t('analytics.bounce.none', 'No bounces or complaints — great list health.')}</p>
          ) : (
            <HBars color="#ef4444" rows={[
              ...bounceRows.map(([k, v]) => ({ label: reasonLabel[k] || k, value: v })),
              ...(data.totals.complained ? [{ label: t('analytics.complained', 'Complaints'), value: data.totals.complained }] : []),
            ]} />
          )}
        </Card>
      </div>
      <p className="mt-3 text-xs text-gray-400">
        {t('analytics.note', 'Opens are indicative — some email clients block tracking and others prefetch, which can under- or over-count.')}
      </p>
    </>
  );
}

/* ---------- page shell ---------- */

export function AnalyticsPage() {
  const { t, i18n } = useTranslation('common');
  const [tab, setTab] = useState<'overview' | 'badges' | 'recipients' | 'geography' | 'email'>('overview');
  const [range, setRange] = useState<Range>('30d');
  const tabs = ['overview', 'badges', 'recipients', 'geography', 'email'] as const;
  const tabLabel: Record<typeof tabs[number], string> = {
    overview: t('analytics.tab.overview', 'Overview'),
    badges: t('analytics.tab.badges', 'Badges'),
    recipients: t('analytics.tab.recipients', 'Recipients'),
    geography: t('analytics.tab.geography', 'Geography'),
    email: t('analytics.tab.email', 'Email'),
  };

  // Export the active tab's data as CSV (React Query dedupes these reads — no extra request).
  const { data: adata } = useAnalytics(range);
  function exportCsv() {
    const stamp = `${range}`;
    if (tab === 'badges') {
      downloadCsv(`badges-${stamp}.csv`, [t('analytics.badges.name', 'Badge'), t('analytics.kpi.issued', 'Issued'), t('analytics.act.view', 'Views'), t('analytics.kpi.shares', 'Shares')],
        (adata?.topBadges ?? []).map(b => [b.name || '—', b.issued, b.views, b.shares]));
    } else if (tab === 'recipients') {
      downloadCsv(`recipients-${stamp}.csv`, [t('analytics.recipients.name', 'Recipient'), t('analytics.act.view', 'Views'), t('analytics.kpi.shares', 'Shares')],
        (adata?.topRecipients ?? []).map(r => [r.name || '—', r.views, r.shares]));
    } else if (tab === 'geography') {
      downloadCsv(`geography-${stamp}.csv`, [t('analytics.geo.country', 'Country'), 'Code', t('analytics.act.view', 'Views'), t('analytics.kpi.shares', 'Shares')],
        (adata?.geography ?? []).map(g => [countryName(g.cc, i18n.language), g.cc, g.views, g.shares]));
    } else {
      // Overview / Email → the daily time series (the most useful raw export).
      downloadCsv(`analytics-${stamp}.csv`,
        ['Date', t('analytics.kpi.issued', 'Issued'), t('analytics.kpi.delivered', 'Delivered'), t('analytics.act.view', 'Views'), t('analytics.kpi.shares', 'Shares'), t('analytics.act.verify', 'Verify clicks')],
        (adata?.series ?? []).map(s => [s.date, s.issued, s.delivered, s.view, s.share, s.verify]));
    }
  }
  const ranges: Range[] = ['7d', '30d', '90d', '365d'];
  const rangeLabel: Record<Range, string> = {
    '7d': t('analytics.range7', '7 days'),
    '30d': t('analytics.range30', '30 days'),
    '90d': t('analytics.range90', '90 days'),
    '365d': t('analytics.range365', '12 months'),
  };

  return (
    <div className="page-wide px-4 py-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('analytics.titleFull', 'Analytics')}</h1>
          <p className="mt-1 text-sm text-gray-500">{t('analytics.subtitleFull', 'How your credentials are issued, delivered, viewed, and shared.')}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5 text-sm">
            {ranges.map(r => (
              <button key={r} onClick={() => setRange(r)}
                className={`rounded-md px-3 py-1 font-medium transition-colors ${range === r ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
                {rangeLabel[r]}
              </button>
            ))}
          </div>
          <button onClick={exportCsv}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50"
            title={t('analytics.exportHint', 'Download the current view as CSV')}>
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6">
              <path d="M10 3v9m0 0l-3.5-3.5M10 12l3.5-3.5M4 15v1.5A1.5 1.5 0 005.5 18h9a1.5 1.5 0 001.5-1.5V15" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {t('analytics.export', 'Export CSV')}
          </button>
        </div>
      </div>

      <div className="mt-5 flex gap-1 overflow-x-auto border-b border-gray-200">
        {tabs.map(tb => (
          <button key={tb} onClick={() => setTab(tb)}
            className={`-mb-px whitespace-nowrap border-b-2 px-4 py-2 text-sm font-semibold transition-colors ${tab === tb ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {tabLabel[tb]}
          </button>
        ))}
      </div>

      {tab === 'overview' && <Overview range={range} />}
      {tab === 'badges' && <BadgesTab range={range} />}
      {tab === 'recipients' && <RecipientsTab range={range} />}
      {tab === 'geography' && <GeographyTab range={range} />}
      {tab === 'email' && <EmailTab range={range} />}
    </div>
  );
}
